import useApiClient from "hooks/useApiClient";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useFlightSearchParams from "./useFlightSearchParams";

// Matches the Angular reference (flight-calendar.component.ts:
// `this.request["range"] = 3`) - a single combined calendar request per
// provider (real Amadeus round-trip pricing, not two summed one-way legs),
// with range always 3.
const REQUESTED_RANGE = 3;

const getRecTotal = (rec) => {
    if (!rec) return null;
    const total = rec?.price?.detailed_price?.total ?? rec?.price?.total;
    return total == null ? null : Number(total);
};

const useGetFlightCalendar = () => {
    const { api, urls } = useApiClient();
    const { i18n, t } = useTranslation();
    const { prepareAvailabilityPayload } = useFlightSearchParams();

    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState(null);
    const [calendarData, setCalendarData] = useState(null);

    const getCalendarData = async () => {
        setCalendarLoading(true);
        setCalendarError(null);
        setCalendarData(null);

        const availabilityPayload = prepareAvailabilityPayload();
        const isDirectOnly = availabilityPayload.directFlight === true || availabilityPayload.directFlight === "true";
        const calendarPayload = { ...availabilityPayload, range: String(REQUESTED_RANGE) };

        try {
            const providersRes = await api.get(urls.flight.providers);
            if (providersRes.status !== 200 || !providersRes.data?.length) {
                setCalendarError(t("noFlightsAvailable"));
                return;
            }

            const recommendationsByKey = {};
            const numberOfStopsOutbound = [];
            const numberOfStopsInbound = [];
            const airlineSet = new Set();
            let currency = null;
            let successCount = 0;

            const fetchOne = async (provider, forceDirect) => {
                const payload = {
                    ...calendarPayload,
                    directFlight: forceDirect ? "true" : calendarPayload.directFlight,
                    provider_id: provider.id,
                    provider_code: provider.code,
                    language: i18n.language,
                };
                try {
                    const queryParams = new URLSearchParams(payload).toString();
                    const res = await api.get(`${urls.flight.calendar}?${queryParams}`);
                    const result = res?.data?.result;
                    if (
                        res.status === 204 ||
                        res.status === 500 ||
                        !result ||
                        !result.recommendations ||
                        result?.error
                    ) {
                        return;
                    }
                    currency = currency || result.currency || null;
                    Object.keys(result.recommendations).forEach((key) => {
                        const rec = result.recommendations[key];
                        const total = getRecTotal(rec);
                        if (total == null) return;
                        rec.flightNeedExchangeDocuments = !!result.flightNeedExchangeDocuments;

                        if (!recommendationsByKey[key]) recommendationsByKey[key] = [];
                        recommendationsByKey[key].push(rec);
                        recommendationsByKey[key].sort(
                            (a, b) => (getRecTotal(a) ?? Infinity) - (getRecTotal(b) ?? Infinity)
                        );

                        if (rec.bound_1 != null) numberOfStopsOutbound[rec.bound_1] = true;
                        if (rec.bound_2 != null) numberOfStopsInbound[rec.bound_2] = true;
                        if (rec.validatingCarrier) airlineSet.add(rec.validatingCarrier);
                    });
                    successCount++;
                } catch (err) {
                    console.error("Error fetching calendar for provider", provider?.code, err);
                }
            };

            await Promise.all(
                providersRes.data.map(async (provider) => {
                    await fetchOne(provider, false);
                    if (!isDirectOnly) {
                        await fetchOne(provider, true);
                    }
                })
            );

            if (successCount === 0 || Object.keys(recommendationsByKey).length === 0) {
                setCalendarError(t("noFlightsAvailable"));
                return;
            }

            setCalendarData({
                recommendationsByKey,
                numberOfStopsOutbound,
                numberOfStopsInbound,
                airlines: Array.from(airlineSet),
                currency,
                range: REQUESTED_RANGE,
            });
        } catch (err) {
            console.error("Error fetching flight calendar:", err);
            setCalendarError("An error has occurred, please try again later.");
        } finally {
            setCalendarLoading(false);
        }
    };

    return { calendarLoading, calendarError, calendarData, getCalendarData };
};

export default useGetFlightCalendar;
